import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  FileText, 
  Calendar,
  ArrowLeft,
  CheckCircle2,
  Clock,
  Eye
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;

const ReportHistory = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    loadReports();
  }, [user, navigate]);

  const loadReports = async () => {
    try {
      const response = await axios.get(
        `${API}/api/strategy_interview/reports`,
        { withCredentials: true }
      );
      
      setReports(response.data.reports || []);
    } catch (error) {
      console.error('Failed to load reports:', error);
      toast.error('Failed to load report history');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (report) => {
    if (report.feedback_submitted) {
      return <Badge variant="default" className="bg-green-600">Feedback Complete</Badge>;
    }
    return <Badge variant="outline">Generated</Badge>;
  };

  const getFramingLabel = (framing) => {
    const labels = {
      'yes_active': 'Active CPA Relationship',
      'yes_filing_only': 'Filing Only',
      'had_previously': 'Previous CPA',
      'looking': 'Looking for CPA',
      'no_plans': 'Self-Directed'
    };
    return labels[framing] || framing;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading report history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Dashboard
              </Button>
              
              <div className="h-6 w-px bg-gray-300"></div>
              
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Report History</h1>
                <p className="text-sm text-gray-600">All generated tax strategy reports</p>
              </div>
            </div>
            
            <Button
              onClick={() => navigate('/strategy-advisor')}
              className="flex items-center gap-2"
            >
              <FileText className="h-4 w-4" />
              New Report
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {reports.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Reports Yet</h3>
              <p className="text-gray-600 mb-6">
                Complete the tax strategy interview to generate your first report.
              </p>
              <Button onClick={() => navigate('/strategy-advisor')}>
                Start Interview
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-600">
                {reports.length} {reports.length === 1 ? 'report' : 'reports'} generated
              </p>
            </div>
            
            {reports.map((report) => {
              const header = report.report_content?.header || {};
              const snapshot = report.interview_snapshot || {};
              const generatedDate = new Date(report.generated_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              });
              
              return (
                <Card key={report.report_id} className="hover:shadow-md transition-shadow">
                  <CardHeader className="border-b border-gray-100">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <CardTitle className="text-lg font-semibold text-gray-900">
                            {header.report_title || 'Tax Strategy Report'}
                          </CardTitle>
                          <Badge variant="outline">v{report.version}</Badge>
                          {getStatusBadge(report)}
                        </div>
                        
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {generatedDate}
                          </div>
                          <div className="flex items-center gap-1">
                            <CheckCircle2 className="h-4 w-4" />
                            {snapshot.completeness_at_generation?.toFixed(1) || 0}% Complete
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="pt-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-1">CPA Status</p>
                        <p className="text-sm text-gray-900">
                          {getFramingLabel(header.framing_variant)}
                        </p>
                      </div>
                      
                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-1">Topics</p>
                        <p className="text-sm text-gray-900">
                          {report.report_content?.topics?.length || 0} discussion topics
                        </p>
                      </div>
                      
                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-1">IRS Sources</p>
                        <p className="text-sm text-gray-900">
                          {report.rag_sources_used?.length || 0} publications referenced
                        </p>
                      </div>
                    </div>
                    
                    {report.feedback_submitted && report.feedback_data && (
                      <div className="p-3 bg-green-50 rounded-lg border border-green-200 mb-4">
                        <div className="flex items-center gap-2 text-sm text-green-800">
                          <CheckCircle2 className="h-4 w-4" />
                          <span className="font-medium">Feedback submitted:</span>
                          <span>
                            {new Date(report.feedback_submitted_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-3">
                      <Button
                        variant="default"
                        onClick={() => navigate(`/strategy-advisor/report/${report.report_id}`)}
                        className="flex items-center gap-2"
                      >
                        <Eye className="h-4 w-4" />
                        View Report
                      </Button>
                      
                      {!report.feedback_submitted && (
                        <Button
                          variant="outline"
                          onClick={() => navigate(`/strategy-advisor/report/${report.report_id}?feedback=true`)}
                          className="flex items-center gap-2"
                        >
                          <Clock className="h-4 w-4" />
                          Add Feedback
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportHistory;
